

import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FacebookSocialCard from "./components/FacebookSocialCard";
import YouTubeSocialCard from "./components/YouTubeSocialCard";
import InstagramSocialCard from "./components/InstagramSocialCard";
import SocialPopup from "./components/SocialPopup";
import ProfileService from "services/profile-service";
import WorkspaceService from "services/workspace-service";
import Toolbar from "./components/toolbar";
import SocialStashGuidanceStep from "layouts/guidance/steps/SocialStashGuidanceStep";


const formatCount = (count) => {
  if (count >= 1_000_000_000) return `${(count / 1_000_000_000).toFixed(1)}B`;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
};



// Tutorial Imports
import SocialStashIntroStep from "layouts/tutorial/steps/SocialStashIntroStep";
import { AuthContext } from "context";
import AuthService from "services/auth-service";
import MDSnackbar from "components/MDSnackbar";

function SocialStash({ isModalView = false }) {
  const [facebookProfiles, setFacebookProfiles] = useState([]);
  const [youtubeProfiles, setYouTubeProfiles] = useState([]);
  const [instagramProfiles, setInstagramProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Snackbar State
  const [errorSB, setErrorSB] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const openErrorSB = (msg) => { setErrorMsg(msg); setErrorSB(true); };
  const closeErrorSB = () => setErrorSB(false);

  // Tutorial State
  const { isAuthenticated } = React.useContext(AuthContext); // Ensure Context is used
  const isLiveDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer"; // [NEW]
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // [NEW] Live Demo Session Guidance State (Attempts Counter)
  const [demoAttempts, setDemoAttempts] = useState(() => {
    const stored = sessionStorage.getItem('demo_stash_attempts');
    return stored ? parseInt(stored, 10) : 0;
  });

  // Increment attempt counter when user clicks Analyse
  const handleDemoAnalyze = () => {
    if (isLiveDemo) {
      const newAttempts = demoAttempts + 1;
      sessionStorage.setItem('demo_stash_attempts', newAttempts.toString());
      setDemoAttempts(newAttempts);

      // [NEW] Mark this module as completed for the tour
      sessionStorage.setItem("demo_visited_stash", "true");
      window.dispatchEvent(new Event('demoVisitUpdated'));
    }
  };

  // Determine current guidance step based on attempts
  // Attempt 0: Initial Visit (Select FB) -> show step 0
  // Attempt 1: Second Visit (Select FB+IG) -> show step 1
  // Attempt 2+: No guidance -> show null
  const demoStep = (!isLiveDemo || demoAttempts >= 2) ? null : demoAttempts;

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTutorial = async () => {
      try {
        // We need the full profile to check completed_tutorials, 
        // reusing the fetch call logic or just fetching profile here separately for safety.
        // Given the existing fetchProfiles does a lot, let's keep this check simple and separate 
        // or integrate into the existing one. For safety, separate:
        const resp = await AuthService.getProfile();
        const userData = resp.data.attributes;
        setCurrentUser(userData);

        const completedSteps = userData.completed_tutorials || [];
        if (!completedSteps.includes("social_stash_intro") && !isLiveDemo) {
          setShowTutorial(true);
        }
      } catch (error) {
        console.error("Failed to check tutorial status:", error);
      }
    };
    checkTutorial();
  }, [isAuthenticated]);

  const handleCompleteTutorial = async () => {
    try {
      const currentCompleted = currentUser?.completed_tutorials || [];
      if (!currentCompleted.includes("social_stash_intro")) {
        const updatedTutorials = [...currentCompleted, "social_stash_intro"];
        await AuthService.updateProfile({
          data: { attributes: { completed_tutorials: updatedTutorials } }
        });
      }
      setShowTutorial(false);
    } catch (error) {
      console.error("Failed to complete tutorial:", error);
      setShowTutorial(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      setLoading(true);

      // STEP 1: Fetch user profile metadata (contains OWN flag)
      const workspaceProfiles = await WorkspaceService.fetchWorkspaceProfiles();

      // Build lookup: id
      const ownMap = {};
      workspaceProfiles.forEach((up) => {
        ownMap[up.id] = up.own === true;
      });

      // Build lookup: id → createdAt
      const createdAtMap = {};
      workspaceProfiles.forEach((up) => {
        createdAtMap[up.id] = up.createdAt;
      });

      const getProfileIdsByPlatform = (platform) =>
        workspaceProfiles.filter((u) => u.platform === platform).map((u) => u.id);

      const facebookIds = getProfileIdsByPlatform("Facebook");
      const youtubeIds = getProfileIdsByPlatform("YouTube");
      const instagramIds = getProfileIdsByPlatform("Instagram");

      // STEP 2: Fetch Facebook profiles
      if (facebookIds.length > 0) {
        const fbProfiles = await ProfileService.fetchProfile("Facebook", facebookIds).catch(() => []);
        const enriched = fbProfiles.map((p) => ({
          ...p,
          own: ownMap[p.id] || false,
          createdAt: createdAtMap[p.id] || null,
        }));
        enriched.sort((a, b) => {
          if (a.own === b.own) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return a.own ? -1 : 1;
        });
        setFacebookProfiles(enriched);
      }

      // STEP 3: Fetch YouTube profiles
      if (youtubeIds.length > 0) {
        const ytProfiles = await ProfileService.fetchProfile("YouTube", youtubeIds).catch(() => []);
        const enriched = ytProfiles.map((p) => ({
          ...p,
          own: ownMap[p.id] || false,
          createdAt: createdAtMap[p.id] || null,
        }));
        enriched.sort((a, b) => {
          if (a.own === b.own) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return a.own ? -1 : 1;
        });
        setYouTubeProfiles(enriched);
      }

      // STEP 4: Fetch Instagram profiles
      if (instagramIds.length > 0) {
        const igProfiles = await ProfileService.fetchProfile("Instagram", instagramIds).catch(() => []);
        const enriched = igProfiles.map((p) => ({
          ...p,
          own: ownMap[p.id] || false,
          createdAt: createdAtMap[p.id] || null,
        }));
        enriched.sort((a, b) => {
          if (a.own === b.own) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return a.own ? -1 : 1;
        });
        setInstagramProfiles(enriched);
      }
    } catch (err) {
      setFacebookProfiles([]);
      setYouTubeProfiles([]);
      setInstagramProfiles([]);
      console.error("Error fetching profiles:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();

    // ✅ Listen for workspace switches (soft refresh)
    const onProfileUpdated = () => fetchProfiles();
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  const filterProfiles = (profiles) =>
    profiles.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()));

  // ⭐ UPDATED: SelectedProfiles now includes OWN attribute
  const handleProfileSelect = (profileId, name, profilePictureUrl, link, platform, own) => {
    setSelectedProfiles((prev) => {
      const exists = prev.some((p) => p.id === profileId && p.platform === platform);

      return exists
        ? prev.filter((p) => !(p.id === profileId && p.platform === platform))
        : [...prev, { id: profileId, platform, name, profilePictureUrl, link, own }];
    });
  };

  const clearSelection = () => setSelectedProfiles([]);

  const handleDeleteProfiles = async () => {
    if (selectedProfiles.length === 0) return;

    try {
      const grouped = selectedProfiles.reduce((acc, p) => {
        if (!acc[p.platform]) acc[p.platform] = [];
        acc[p.platform].push(p.id);
        return acc;
      }, {});

      for (const [platform, ids] of Object.entries(grouped)) {
        await WorkspaceService.deleteWorkspaceProfiles(ids, platform);
      }

      setFacebookProfiles((prev) => prev.filter((p) => !selectedProfiles.some((sp) => sp.id === p.id)));
      setYouTubeProfiles((prev) => prev.filter((p) => !selectedProfiles.some((sp) => sp.id === p.id)));
      setInstagramProfiles((prev) => prev.filter((p) => !selectedProfiles.some((sp) => sp.id === p.id)));

      setSelectedProfiles([]);
    } catch (err) {
      console.error("Error deleting profiles:", err);
      const msg = err.response?.data?.message || "Failed to delete profiles. Please try again.";
      openErrorSB(msg);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toolbar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      {/* 🟢 DEMO GUIDANCE */}
      <SocialStashGuidanceStep
        isLiveDemo={isLiveDemo}
        demoStep={demoStep}
        showTutorial={showTutorial}
      />

      <MDBox pt={4} pb={4} position="relative"> {/* Relative for z-index context */}

        <Grid container spacing={6}>
          {/* Facebook */}
          <Grid item xs={12}>
            <FacebookSocialCard
              profiles={filterProfiles(facebookProfiles)}
              loading={loading}
              error={error}
              selectedProfiles={selectedProfiles}
              formatCount={formatCount}
              // 🔵 DEMO MODE: 
              // Step 0: Highlight if nothing selected
              // Step 1: Highlight if FB not yet selected (and nothing else selected initially, or to guide cross-platform)
              demoMode={isLiveDemo && (
                (demoStep === 0 && selectedProfiles.length === 0) ||
                (demoStep === 1 && !selectedProfiles.some(p => p.platform === "Facebook"))
              )}
              demoTooltip={demoStep === 1 ? "Select a Facebook Profile first" : "Click on the profile to select it"}
              onProfileSelect={(id, name, pic, link) =>
                handleProfileSelect(
                  id,
                  name,
                  pic,
                  link,
                  "Facebook",
                  facebookProfiles.find((p) => p.id === id)?.own || false
                )
              }
            />
          </Grid>

          {/* YouTube */}
          <Grid item xs={12}>
            <YouTubeSocialCard
              profiles={filterProfiles(youtubeProfiles)}
              loading={loading}
              error={error}
              selectedProfiles={selectedProfiles}
              formatCount={formatCount}
              onProfileSelect={(id, name, pic, link) =>
                handleProfileSelect(
                  id,
                  name,
                  pic,
                  link,
                  "YouTube",
                  youtubeProfiles.find((p) => p.id === id)?.own || false
                )
              }
            />
          </Grid>

          {/* Instagram */}
          <Grid item xs={12}>
            <InstagramSocialCard
              profiles={filterProfiles(instagramProfiles)}
              loading={loading}
              error={error}
              selectedProfiles={selectedProfiles}
              formatCount={formatCount}
              // 🔵 DEMO MODE Step 1: Highlight if FB is selected but IG is NOT
              demoMode={isLiveDemo && demoStep === 1 && selectedProfiles.some(p => p.platform === "Facebook") && !selectedProfiles.some(p => p.platform === "Instagram")}
              demoTooltip="Now Select an Instagram Profile"
              onProfileSelect={(id, name, pic, link) =>
                handleProfileSelect(
                  id,
                  name,
                  pic,
                  link,
                  "Instagram",
                  instagramProfiles.find((p) => p.id === id)?.own || false
                )
              }
            />
          </Grid>
        </Grid>
      </MDBox>

      <SocialPopup
        selectedProfiles={selectedProfiles}
        onDeleteConfirm={handleDeleteProfiles}
        onCancel={clearSelection}
        // 🔵 DEMO MODE: Highlight Analyze button if profiles selected
        // Step 0: Any selection > 0
        // Step 1: Must have FB AND IG selected
        demoMode={isLiveDemo && (
          (demoStep === 0 && selectedProfiles.length > 0) ||
          (demoStep === 1 && selectedProfiles.some(p => p.platform === "Facebook") && selectedProfiles.some(p => p.platform === "Instagram"))
        )}
        // If we are in Step 1, block normal highlighting until both are selected, 
        // effectively making "Analyse" highlight only available when requirements met.
        // Actually, SocialPopup demoMode just highlights it. 
        // We might want to pass handleDemoAnalyze to mark step 0 done.
        onDemoAnalyze={handleDemoAnalyze}
      />

      <Footer />

      {/* Social Stash Tutorial Popup */}
      <SocialStashIntroStep
        open={showTutorial}
        onComplete={handleCompleteTutorial}
        onSkip={() => setShowTutorial(false)}
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={errorMsg}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default SocialStash;
